import { ActionIconDef, GridConfig, GridRow, ToolbarActionDef } from '@inavista/components/grid';
import { LOCAL_ROWS_URL, LocalRowsGridApi } from 'projects/service/src/app/shared/utils/local-rows-grid-api';

// General section → Addresses, on @inavista/components. The rows are the profile's addresses the section already holds
// (UserProfile/GetUserProfileAddressById), changed in the page and saved with the profile's Save, as before: they go
// through LocalRowsGridApi (the approved local-rows seam), no endpoint. Deleted addresses stay in the list sent on Save
// (isDeleted) and are not shown, as before.

export const UP_ADDRESSES_URL = `${LOCAL_ROWS_URL}user-profile/general-addresses`;

const COLUMNS = ['addressCode', 'name', 'telephone', 'email', 'address'];

export const UP_ADDRESSES_SCHEMA = LocalRowsGridApi.schema([
  ['addressCode', 'varchar'], ['name', 'varchar'], ['telephone', 'varchar'], ['email', 'varchar'], ['address', 'varchar'],
  ['city', 'varchar'], ['state', 'varchar'], ['zipCode', 'varchar'], ['country', 'varchar'], ['isDefault', 'bit'], ['isLNPreferred', 'bit'],
]);

export interface UpAddressesContext {
  makeDefault: { visible: boolean; enable: boolean };
  remove: { visible: boolean; enable: boolean };
  add: { visible: boolean; enable: boolean };
  canExport: boolean;
}

const ICON = {
  makeDefault: '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />',
  remove: '<polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />',
  add: '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />',
};
const GREY = '#ccc';
const NO_PERMISSION = 'your role does not allow this';

// Make Default (green on the default address, greyed there) and Delete (greyed on the default and on the LN-preferred
// address), each shown and enabled by its permission, as before.
export function upAddressesActionIcons(ctx: UpAddressesContext): ActionIconDef[] {
  return [
    ...(ctx.makeDefault.visible ? [{
      key: 'makeDefault', label: 'Make Default Address', icon: ICON.makeDefault,
      colorFn: (row: GridRow) => (row['isDefault'] ? '#28a745' : ctx.makeDefault.enable ? '#6c757d' : GREY),
      disabledFn: (row: GridRow) => !ctx.makeDefault.enable || !!row['isDefault'],
      labelFn: (row: GridRow) => (row['isDefault'] ? 'Default address'
        : ctx.makeDefault.enable ? 'Make Default Address' : `Make Default Address: ${NO_PERMISSION}`),
    }] : []),
    ...(ctx.remove.visible ? [{
      key: 'delete', label: 'Delete', icon: ICON.remove,
      colorFn: (row: GridRow) => (!ctx.remove.enable || row['isDefault'] || row['isLNPreferred'] ? GREY : '#000000de'),
      disabledFn: (row: GridRow) => !ctx.remove.enable || !!row['isDefault'] || !!row['isLNPreferred'],
      labelFn: (row: GridRow) => (!ctx.remove.enable ? `Delete: ${NO_PERMISSION}`
        : row['isDefault'] ? 'The default address cannot be deleted'
        : row['isLNPreferred'] ? 'The LN preferred address cannot be deleted' : 'Delete'),
    }] : []),
  ];
}

export function upAddressesToolbarActions(ctx: UpAddressesContext): ToolbarActionDef[] {
  return ctx.add.visible
    ? [{ key: 'addAddress', label: ctx.add.enable ? 'Add New Address' : `Add New Address: ${NO_PERMISSION}`, icon: ICON.add,
         kind: 'button', position: 'start', disabled: !ctx.add.enable }]
    : [];
}

export function upAddressesGridConfig(ctx: UpAddressesContext): GridConfig {
  return {
    apiBaseUrl: UP_ADDRESSES_URL,
    keyColumn: 'addressCode',
    clientSide: true,
    rowHeight: 26,
    stretchColumns: true,
    enableSearch: true,
    enableContextMenuFilter: true,
    enableFitColumns: true,
    enableResetLayout: true,
    enableExport: ctx.canExport,
    defaultColumnOrder: COLUMNS,
    hiddenByDefaultColumns: ['city', 'state', 'zipCode', 'country', 'isDefault', 'isLNPreferred'],
    columnLabels: { addressCode: 'Address Code', name: 'Name', telephone: 'Telephone', email: 'Email', address: 'Address Description' },
    columnWidths: { addressCode: 120, name: 120, telephone: 80, email: 120, address: 190 },
    columnAlign: Object.fromEntries(COLUMNS.map(c => [c, {
      header: { horizontal: 'left' as const },
      ...(c === 'addressCode' ? { horizontal: 'center' as const } : {}),
    }])),
    // As before: the address followed by its city, state, zip code and country.
    columnFormatters: {
      address: (_value, row) => [row['address'], row['city'], row['state'], row['zipCode'], row['country']]
        .filter(v => v !== null && v !== undefined && v !== '').join(', '),
    },
    emptyStateMessage: 'No addresses yet. Add one with the + button.',
    persistenceKey: 'rims.users.general-addresses',
    persistenceVersion: 1,
  };
}
